import { useEffect, useMemo, useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  Check,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  EllipsisVertical,
  Plus,
  Save,
  Trash2,
  X,
} from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  useCreateCourseMarkingSchemeMutation,
  useCreateCourseStructureMutation,
  useCreateCourseSubStructureMutation,
  useCreateMultipleCourseAcquiredMarksMutation,
  useDeleteCourseMarkingSchemeMutation,
  useGetCourseAcquiredMarksQuery,
  useGetEnrolledStudentsQuery,
  useGetCourseMarkingSchemeQuery,
  useGetCourseStructuresQuery,
  usePatchCourseMarkingSchemeMutation,
  usePatchCourseSubStructureMutation,
  useUpdateMultipleCourseAcquiredMarksMutation,
} from '@/state/course';
import type { CourseMarkingSchemeItem } from '@/state/course';
import SkillFrameworkTree from '@/pages/admin-tools/components/skill-framework-tree';
import SkillFrameworkSunburst from '@/pages/admin-tools/components/skill-framework-sunburst';
import type { SkillNode } from '@/pages/admin-tools/components/skill-framework-chart-utils';

interface CourseAssessmentStructureContentProps {
  tenant: string;
  courseId: number;
  canEdit: boolean;
}

type ChartViewMode = 'list' | 'tree' | 'sunburst';

type SubStructureEditState = {
  subStructureId: number;
  markingSchemeId: number;
  structureId: number;
  title: string;
  fullMarks: number;
  weightage: number;
};

type StudentMarkDraft = {
  enrollmentId: number;
  studentId: string;
  name: string;
  email: string;
  marks: string;
};

type MarksBaseline = Record<number, string>;

const toChartNode = (id: number, name: string, children: SkillNode[] = []): SkillNode => ({
  id,
  name,
  description: '-',
  published: true,
  level: 1,
  children,
});

const CourseAssessmentStructureContent = ({
  tenant,
  courseId,
  canEdit,
}: CourseAssessmentStructureContentProps) => {
  const { data: markingSchemeRaw = [] } = useGetCourseMarkingSchemeQuery(
    tenant ? { tenant, courseId } : skipToken
  );
  const { data: enrolledStudentsRaw = [] } = useGetEnrolledStudentsQuery(
    tenant ? { tenant, courseId } : skipToken
  );
  const { data: courseStructuresRaw = [] } = useGetCourseStructuresQuery(
    tenant ? { tenant } : skipToken
  );
  const structureNameById = useMemo(
    () => new Map(courseStructuresRaw.map((item) => [item.id, item.assessment_type] as const)),
    [courseStructuresRaw]
  );

  const [createCourseStructure, { isLoading: isCreatingCourseStructure }] =
    useCreateCourseStructureMutation();
  const [createCourseMarkingScheme, { isLoading: isCreatingMarkingScheme }] =
    useCreateCourseMarkingSchemeMutation();
  const [patchCourseMarkingScheme, { isLoading: isPatchingMarkingScheme }] =
    usePatchCourseMarkingSchemeMutation();
  const [deleteCourseMarkingScheme, { isLoading: isDeletingMarkingScheme }] =
    useDeleteCourseMarkingSchemeMutation();
  const [createCourseSubStructure, { isLoading: isCreatingSubStructure }] =
    useCreateCourseSubStructureMutation();
  const [patchCourseSubStructure, { isLoading: isPatchingSubStructure }] =
    usePatchCourseSubStructureMutation();
  const [createMultipleCourseAcquiredMarks, { isLoading: isUpdatingMarks }] =
    useCreateMultipleCourseAcquiredMarksMutation();
  const [updateMultipleCourseAcquiredMarks] = useUpdateMultipleCourseAcquiredMarksMutation();

  const [expandedTopIds, setExpandedTopIds] = useState<Record<number, boolean>>({});
  const [chartViewMode, setChartViewMode] = useState<ChartViewMode>('list');

  const [editingTopId, setEditingTopId] = useState<number | null>(null);
  const [topNameDraft, setTopNameDraft] = useState('');
  const [topWeightDraft, setTopWeightDraft] = useState('');

  const [isAddingTopRow, setIsAddingTopRow] = useState(false);
  const [newTypeName, setNewTypeName] = useState('');
  const [newTypeWeight, setNewTypeWeight] = useState('');

  const [subStructureDialog, setSubStructureDialog] = useState<{
    open: boolean;
    mode: 'add' | 'edit';
    structureId: number | null;
    parentTopWeight: number;
    editState: SubStructureEditState | null;
  }>({
    open: false,
    mode: 'add',
    structureId: null,
    parentTopWeight: 0,
    editState: null,
  });
  const [subStructureTitleDraft, setSubStructureTitleDraft] = useState('');
  const [subStructureFullMarksDraft, setSubStructureFullMarksDraft] = useState('');
  const [subStructureWeightageDraft, setSubStructureWeightageDraft] = useState('');

  const [markingSchemeIdToDelete, setMarkingSchemeIdToDelete] = useState<number | null>(null);
  const [subMarkingSchemeIdToDelete, setSubMarkingSchemeIdToDelete] = useState<number | null>(
    null
  );
  const [marksDialogOpen, setMarksDialogOpen] = useState(false);
  const [marksDialogMarkingScheme, setMarksDialogMarkingScheme] =
    useState<CourseMarkingSchemeItem | null>(null);
  const [studentMarkDrafts, setStudentMarkDrafts] = useState<StudentMarkDraft[]>([]);
  const [initialMarksByEnrollmentId, setInitialMarksByEnrollmentId] = useState<MarksBaseline>({});
  const [marksSearch, setMarksSearch] = useState('');
  const [confirmDefaultMarksOpen, setConfirmDefaultMarksOpen] = useState(false);

  const topRows = useMemo(
    () => markingSchemeRaw.filter((item) => item.course_sub_structure === null),
    [markingSchemeRaw]
  );

  const totalWeight = useMemo(
    () => topRows.reduce((acc, item) => acc + Number(item.course_percentage || 0), 0),
    [topRows]
  );

  useEffect(() => {
    setExpandedTopIds((prev) => {
      const next = { ...prev };
      topRows.forEach((row) => {
        const isAttendance = row.course_structure.assessment_type === 'Attendance';
        if (!isAttendance && typeof next[row.id] !== 'boolean') {
          next[row.id] = false;
        }
      });
      return next;
    });
  }, [topRows]);

  const rows = useMemo(
    () =>
      topRows.map((top) => ({
        top,
        children: markingSchemeRaw.filter(
          (item) =>
            item.course_sub_structure !== null && item.course_structure.id === top.course_structure.id
        ),
      })),
    [markingSchemeRaw, topRows]
  );

  const enrolledStudentDrafts = useMemo<StudentMarkDraft[]>(
    () =>
      enrolledStudentsRaw.map((entry) => ({
        enrollmentId: entry.id,
        studentId: entry.student.id,
        name: `${entry.student.first_name} ${entry.student.last_name}`.trim() || entry.student.email,
        email: entry.student.email,
        marks: '',
      })),
    [enrolledStudentsRaw]
  );

  const { data: existingCourseAcquiredMarks = [] } = useGetCourseAcquiredMarksQuery(
    tenant && marksDialogMarkingScheme
      ? {
          tenant,
          markingScheme: marksDialogMarkingScheme.id,
          courseId,
        }
      : skipToken
  );

  const existingMarksByEnrollmentId = useMemo(
    () =>
      new Map(
        existingCourseAcquiredMarks.map((item) => [item.student_enroll, item] as const)
      ),
    [existingCourseAcquiredMarks]
  );

  useEffect(() => {
    if (!marksDialogOpen || !marksDialogMarkingScheme || !enrolledStudentDrafts.length) return;
    if (studentMarkDrafts.length > 0) return;

    const nextDrafts = enrolledStudentDrafts.map((student) => {
      const existing = existingMarksByEnrollmentId.get(student.enrollmentId);
      const nextMarks =
        existing?.marks !== undefined && existing?.marks !== null ? String(existing.marks) : '';
      return {
        ...student,
        marks: nextMarks,
      };
    });
    const nextBaseline = Object.fromEntries(
      nextDrafts.map((draft) => [draft.enrollmentId, draft.marks] as const)
    );

    const isSame =
      nextDrafts.length === studentMarkDrafts.length &&
      nextDrafts.every(
        (draft, index) =>
          draft.enrollmentId === studentMarkDrafts[index]?.enrollmentId &&
          draft.marks === studentMarkDrafts[index]?.marks
      );

    if (!isSame) {
      setStudentMarkDrafts(nextDrafts);
      setInitialMarksByEnrollmentId(nextBaseline);
    }
  }, [
    enrolledStudentDrafts,
    existingMarksByEnrollmentId,
    marksDialogMarkingScheme,
    marksDialogOpen,
    studentMarkDrafts.length,
  ]);

  const hasUnsavedChanges = useMemo(
    () =>
      studentMarkDrafts.some((student) => {
        const baseline = initialMarksByEnrollmentId[student.enrollmentId] ?? '';
        return student.marks.trim() !== baseline.trim();
      }),
    [initialMarksByEnrollmentId, studentMarkDrafts]
  );

  const resetMarksDialogState = () => {
    setMarksDialogMarkingScheme(null);
    setStudentMarkDrafts([]);
    setInitialMarksByEnrollmentId({});
    setMarksSearch('');
    setConfirmDefaultMarksOpen(false);
  };

  const visibleStudentDrafts = useMemo(() => {
    const query = marksSearch.trim().toLowerCase();
    if (!query) return studentMarkDrafts;
    return studentMarkDrafts.filter((student) => {
      const haystack = `${student.name} ${student.email} ${student.enrollmentId}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [marksSearch, studentMarkDrafts]);

  const chartData = useMemo<SkillNode>(() => {
    const children = rows.map((row) => {
      const sub = row.children.map((item) =>
        toChartNode(
          item.id,
          `${item.marking_structure_name} (${Number(item.course_percentage || 0).toFixed(0)}%)`
        )
      );
      return toChartNode(
        row.top.id,
        `${row.top.marking_structure_name} (${Number(row.top.course_percentage || 0).toFixed(0)}%)`,
        sub
      );
    });
    return toChartNode(0, 'Course structure', children);
  }, [rows]);

  const subStructureDialogTypeLabel = useMemo(() => {
    const structureId =
      subStructureDialog.editState?.structureId ?? subStructureDialog.structureId;
    if (!structureId) return 'Assessment item';
    return (
      structureNameById.get(structureId) ||
      topRows.find((row) => row.course_structure.id === structureId)?.course_structure
        .assessment_type ||
      'Assessment item'
    );
  }, [
    structureNameById,
    subStructureDialog.editState?.structureId,
    subStructureDialog.structureId,
    topRows,
  ]);

  const handleToggleTop = (topId: number) => {
    setExpandedTopIds((prev) => ({
      ...prev,
      [topId]: !(prev[topId] ?? true),
    }));
  };

  const handleOpenTopEdit = (topId: number) => {
    const row = topRows.find((item) => item.id === topId);
    if (!row) return;
    setEditingTopId(topId);
    setTopNameDraft(row.marking_structure_name);
    setTopWeightDraft(String(Number(row.course_percentage || 0)));
  };

  const handleSaveTopEdit = async () => {
    if (!editingTopId) return;
    const trimmedName = topNameDraft.trim();
    if (!trimmedName) {
      toast.error('Title is required.');
      return;
    }
    try {
      await patchCourseMarkingScheme({
        tenant,
        markingSchemeId: editingTopId,
        data: {
          marking_structure_name: trimmedName,
          course_percentage: topWeightDraft,
        },
      }).unwrap();
      toast.success('Assessment updated successfully.');
      setEditingTopId(null);
      setTopNameDraft('');
      setTopWeightDraft('');
    } catch {
      toast.error('Failed to update assessment.');
    }
  };

  const handleDeleteTop = async () => {
    if (!markingSchemeIdToDelete) return;
    try {
      await deleteCourseMarkingScheme({
        tenant,
        markingSchemeId: markingSchemeIdToDelete,
      }).unwrap();
      toast.success('Assessment deleted successfully.');
      setMarkingSchemeIdToDelete(null);
    } catch {
      toast.error('Failed to delete assessment.');
    }
  };

  const handleCreateOtherAssessmentType = async () => {
    const trimmedName = newTypeName.trim();
    if (!trimmedName) {
      toast.error('Assessment type title is required.');
      return;
    }
    const parsedWeight = Number(newTypeWeight);
    if (!Number.isFinite(parsedWeight) || parsedWeight < 0) {
      toast.error('Please enter a valid weightage.');
      return;
    }
    if (totalWeight + parsedWeight > 100) {
      toast.error(`Total weightage cannot exceed 100%. Remaining: ${Math.max(0, 100 - totalWeight).toFixed(2)}%`);
      return;
    }
    try {
      const createdStructure = await createCourseStructure({
        tenant,
        data: {
          assessment_type: trimmedName,
          description: '-',
        },
      }).unwrap();
      await createCourseMarkingScheme({
        tenant,
        data: {
          marking_structure_name: trimmedName,
          description: '-',
          course_percentage: parsedWeight,
          full_marks: 100,
          course: courseId,
          course_structure: createdStructure.id,
          course_sub_structure: null,
        },
      }).unwrap();
      toast.success('Assessment type created successfully.');
      setNewTypeName('');
      setNewTypeWeight('');
      setIsAddingTopRow(false);
    } catch {
      toast.error('Failed to create assessment type.');
    }
  };

  const handleOpenAddSubStructure = (structureId: number, parentTopWeight: number) => {
    setSubStructureDialog({
      open: true,
      mode: 'add',
      structureId,
      parentTopWeight,
      editState: null,
    });
    setSubStructureTitleDraft('');
    setSubStructureFullMarksDraft('100');
    setSubStructureWeightageDraft('');
  };

  const handleOpenEditSubStructure = (
    subStructureId: number,
    markingSchemeId: number,
    structureId: number,
    title: string,
    fullMarks: number,
    weightage: number,
    parentTopWeight: number
  ) => {
    setSubStructureDialog({
      open: true,
      mode: 'edit',
      structureId,
      parentTopWeight,
      editState: {
        subStructureId,
        markingSchemeId,
        structureId,
        title,
        fullMarks,
        weightage,
      },
    });
    setSubStructureTitleDraft(title);
    setSubStructureFullMarksDraft(String(fullMarks));
    setSubStructureWeightageDraft(String(weightage));
  };

  const handleOpenUpdateMarks = (markingScheme: CourseMarkingSchemeItem) => {
    setMarksDialogMarkingScheme(markingScheme);
    setMarksDialogOpen(true);
  };

  const handleSubmitMarks = async (defaultEmptyToZero: boolean) => {
    if (!marksDialogMarkingScheme) return;
    const maxMarks = Number(marksDialogMarkingScheme.full_marks || 0);

    for (const student of studentMarkDrafts) {
      const raw = student.marks.trim();
      if (raw === '') continue;
      const parsed = Number(raw);
      if (!Number.isFinite(parsed) || parsed < 0 || parsed > maxMarks) {
        toast.error('Marks must be between 0 and the substructure full marks.');
        return;
      }
    }

    const createStudents: Array<{
      marks: number | null;
      full_marks: number;
      grade: null;
      status: number;
      marking_scheme: number;
      course: number;
      student_enroll: number;
      student: string;
    }> = [];
    const updateStudents: Array<{
      marks: number | null;
      full_marks: number;
      grade: null;
      status: number;
      marking_scheme: number;
      course: number;
      student_enroll: number;
      student: string;
      id: number;
    }> = [];

    const payloadStudents = studentMarkDrafts.map((student) => {
      const raw = student.marks.trim();
      const parsed = raw === '' ? null : Number(raw);
      const existing = existingMarksByEnrollmentId.get(student.enrollmentId);
      const payload = {
        marks: raw === '' ? (defaultEmptyToZero ? 0 : null) : parsed,
        full_marks: maxMarks,
        grade: null,
        status: 3,
        marking_scheme: marksDialogMarkingScheme.id,
        course: courseId,
        student_enroll: student.enrollmentId,
        student: student.studentId,
        ...(existing?.id ? { id: existing.id } : {}),
      };
      if (existing?.id) {
        updateStudents.push(payload as (typeof updateStudents)[number]);
      } else {
        createStudents.push(payload as (typeof createStudents)[number]);
      }
      return payload;
    });

    if (!defaultEmptyToZero && payloadStudents.some((item) => item.marks === null)) {
      setConfirmDefaultMarksOpen(true);
      return;
    }

    try {
      await Promise.all([
        createStudents.length
          ? createMultipleCourseAcquiredMarks({
              tenant,
              data: { students: createStudents },
            }).unwrap()
          : Promise.resolve(),
        updateStudents.length
          ? updateMultipleCourseAcquiredMarks({
              tenant,
              data: { students: updateStudents },
            }).unwrap()
          : Promise.resolve(),
      ]);
      toast.success('Marks updated successfully.');
      setMarksDialogOpen(false);
      resetMarksDialogState();
    } catch {
      toast.error('Failed to update marks.');
    }
  };

  const handleSaveSubStructure = async () => {
    const trimmedTitle = subStructureTitleDraft.trim();
    if (!trimmedTitle) {
      toast.error('Title is required.');
      return;
    }
    const fullMarks = Number(subStructureFullMarksDraft);
    if (!Number.isFinite(fullMarks) || fullMarks <= 0) {
      toast.error('Please enter valid full marks.');
      return;
    }
    const weightage = Number(subStructureWeightageDraft);
    if (!Number.isFinite(weightage) || weightage < 0) {
      toast.error('Please enter valid weightage.');
      return;
    }
    if (!subStructureDialog.structureId) return;

    const currentSubItems = markingSchemeRaw.filter(
      (item) =>
        item.course_sub_structure !== null && item.course_structure.id === subStructureDialog.structureId
    );
    const existingWeightageSum = currentSubItems.reduce((sum, item) => {
      if (
        subStructureDialog.mode === 'edit' &&
        subStructureDialog.editState &&
        item.id === subStructureDialog.editState.markingSchemeId
      ) {
        return sum;
      }
      return sum + Number(item.course_percentage || 0);
    }, 0);

    if (existingWeightageSum + weightage > subStructureDialog.parentTopWeight) {
      const remaining = Math.max(0, subStructureDialog.parentTopWeight - existingWeightageSum);
      toast.error(
        `Substructure weightage exceeds parent limit (${subStructureDialog.parentTopWeight}%). Remaining: ${remaining.toFixed(2)}%`
      );
      return;
    }

    try {
      if (subStructureDialog.mode === 'add' && subStructureDialog.structureId) {
        const createdSub = await createCourseSubStructure({
          tenant,
          data: {
            sub_structure_type: trimmedTitle,
            description: '-',
            structure: subStructureDialog.structureId,
          },
        }).unwrap();
        await createCourseMarkingScheme({
          tenant,
          data: {
            marking_structure_name: trimmedTitle,
            description: '-',
            course_percentage: weightage,
            course: courseId,
            full_marks: fullMarks,
            course_structure: subStructureDialog.structureId,
            course_sub_structure: createdSub.id,
          },
        }).unwrap();
      } else if (subStructureDialog.mode === 'edit' && subStructureDialog.editState) {
        await patchCourseSubStructure({
          tenant,
          subStructureId: subStructureDialog.editState.subStructureId,
          data: {
            sub_structure_type: trimmedTitle,
            description: '-',
            structure: subStructureDialog.editState.structureId,
          },
        }).unwrap();
        await patchCourseMarkingScheme({
          tenant,
          markingSchemeId: subStructureDialog.editState.markingSchemeId,
          data: {
            marking_structure_name: trimmedTitle,
            course_percentage: weightage,
            full_marks: fullMarks,
          },
        }).unwrap();
      }
      toast.success('Sub-assessment saved successfully.');
      setSubStructureDialog({
        open: false,
        mode: 'add',
        structureId: null,
        parentTopWeight: 0,
        editState: null,
      });
      setSubStructureTitleDraft('');
      setSubStructureFullMarksDraft('');
      setSubStructureWeightageDraft('');
    } catch {
      toast.error('Failed to save sub-assessment.');
    }
  };

  const handleDeleteSubMarkingScheme = async () => {
    if (!subMarkingSchemeIdToDelete) return;
    try {
      await deleteCourseMarkingScheme({
        tenant,
        markingSchemeId: subMarkingSchemeIdToDelete,
      }).unwrap();
      toast.success('Sub-assessment deleted successfully.');
      setSubMarkingSchemeIdToDelete(null);
    } catch {
      toast.error('Failed to delete sub-assessment.');
    }
  };

  return (
    <div className="space-y-4 rounded-xl border bg-card p-4 shadow-sm">
      {Math.abs(totalWeight - 100) > 0.001 ? (
        <div className="flex items-center gap-2 rounded-md border border-amber-300/70 bg-amber-50 px-3 py-2 text-sm text-amber-700 dark:border-amber-700/70 dark:bg-amber-950/40 dark:text-amber-300">
          <AlertTriangle className="h-4 w-4" />
          Total assessment weight is {totalWeight.toFixed(2)}%. It should be 100%.
        </div>
      ) : null}

      <div className="space-y-4">
        <div className="flex justify-end gap-1">
          <Button
            type="button"
            variant={chartViewMode === 'list' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setChartViewMode('list')}
          >
            List
          </Button>
          <Button
            type="button"
            variant={chartViewMode === 'tree' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setChartViewMode('tree')}
          >
            Tree
          </Button>
          <Button
            type="button"
            variant={chartViewMode === 'sunburst' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setChartViewMode('sunburst')}
          >
            Sunburst
          </Button>
        </div>

        {chartViewMode === 'list' ? (
          <div className="space-y-3">
          {rows.map(({ top, children }) => {
            const isAttendance = top.course_structure.assessment_type === 'Attendance';
            const isExpanded = expandedTopIds[top.id] ?? true;
            const isTopEditing = editingTopId === top.id;

            return (
              <div key={top.id} className="rounded-md border bg-muted/25">
                <div className="flex items-center gap-2 px-3 py-2.5">
                  <button
                    type="button"
                    className="flex min-w-0 flex-1 items-center gap-2 text-left"
                    onClick={() => {
                      if (isAttendance) return;
                      handleToggleTop(top.id);
                    }}
                  >
                    {isAttendance ? (
                      <span className="h-4 w-4" />
                    ) : isExpanded ? (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    )}

                    {isTopEditing ? (
                      <div className="grid w-full grid-cols-[minmax(0,1fr)_110px] gap-2" onClick={(e) => e.stopPropagation()}>
                        <Input
                          value={topNameDraft}
                          onChange={(event) => setTopNameDraft(event.target.value)}
                          placeholder="Title"
                          className="h-9"
                        />
                        <Input
                          value={topWeightDraft}
                          onChange={(event) => setTopWeightDraft(event.target.value)}
                          placeholder="Weightage"
                          className="h-9"
                        />
                      </div>
                    ) : (
                      <>
                        <span className="truncate text-[15px] font-medium">{top.marking_structure_name}</span>
                        <span className="ml-auto shrink-0 text-[15px] text-muted-foreground">
                          {Number(top.course_percentage || 0)}%
                        </span>
                      </>
                    )}
                  </button>

                  {isTopEditing ? (
                    <div className="flex items-center gap-1">
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() => void handleSaveTopEdit()}
                        disabled={isPatchingMarkingScheme}
                      >
                        <Save className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() => setEditingTopId(null)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : canEdit ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
                        >
                          <EllipsisVertical className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleOpenTopEdit(top.id)}>
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onClick={() => setMarkingSchemeIdToDelete(top.id)}
                        >
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : null}
                </div>

                {!isAttendance && isExpanded ? (
                  <div className="border-t bg-background/50 px-3 py-2">
                    {canEdit ? (
                      <button
                        type="button"
                        className="mb-2 inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                        onClick={() =>
                          handleOpenAddSubStructure(
                            top.course_structure.id,
                            Number(top.course_percentage || 0)
                          )
                        }
                      >
                        <Plus className="h-3.5 w-3.5" />
                        Add{' '}
                        {structureNameById.get(top.course_structure.id) ||
                          top.course_structure.assessment_type}
                      </button>
                    ) : null}

                    <div className="space-y-1.5">
                      {children.map((child) => (
                        <div
                          key={child.id}
                          className="flex items-center justify-between gap-2 rounded-md border bg-card px-3 py-2"
                        >
                          <p className="truncate text-sm">{child.marking_structure_name}</p>
                          <div className="flex items-center gap-2">
                            <p className="text-sm text-muted-foreground">
                              {Number(child.course_percentage || 0)}%
                            </p>
                            {canEdit && child.course_sub_structure ? (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <button
                                    type="button"
                                    className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
                                  >
                                    <EllipsisVertical className="h-4 w-4" />
                                  </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem
                                    onClick={() =>
                                      handleOpenEditSubStructure(
                                        child.course_sub_structure!.id,
                                        child.id,
                                        child.course_structure.id,
                                        child.course_sub_structure!.sub_structure_type,
                                        Number(child.full_marks || 100),
                                        Number(child.course_percentage || 0),
                                        Number(top.course_percentage || 0)
                                      )
                                    }
                                  >
                                    Edit
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleOpenUpdateMarks(child)}>
                                    Update marks
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    className="text-destructive focus:text-destructive"
                                    onClick={() => setSubMarkingSchemeIdToDelete(child.id)}
                                  >
                                    Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            ) : null}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}

          {canEdit ? (
            isAddingTopRow ? (
              <div className="rounded-md border bg-muted/25 px-3 py-2.5">
                <div className="grid grid-cols-[minmax(0,1fr)_110px_auto_auto] items-center gap-2">
                  <Input
                    value={newTypeName}
                    onChange={(event) => setNewTypeName(event.target.value)}
                    placeholder="Assessment title"
                    className="h-9"
                  />
                  <Input
                    value={newTypeWeight}
                    onChange={(event) => setNewTypeWeight(event.target.value)}
                    placeholder="Weightage"
                    className="h-9"
                  />
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8"
                    onClick={() => void handleCreateOtherAssessmentType()}
                    disabled={isCreatingCourseStructure || isCreatingMarkingScheme}
                  >
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8"
                    onClick={() => {
                      setIsAddingTopRow(false);
                      setNewTypeName('');
                      setNewTypeWeight('');
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                type="button"
                className="w-full justify-start"
                onClick={() => setIsAddingTopRow(true)}
              >
                <Plus className="h-4 w-4" />
                Add other assessment type e.g: Exam or Other
              </Button>
            )
          ) : null}
        </div>
        ) : (
          <div className="space-y-2">
            {chartViewMode === 'tree' ? (
              <SkillFrameworkTree framework={chartData} height={520} />
            ) : (
              <SkillFrameworkSunburst framework={chartData} height={520} />
            )}
          </div>
        )}
      </div>

      <Dialog
        open={subStructureDialog.open}
        onOpenChange={(open) =>
          setSubStructureDialog((prev) =>
            open
              ? { ...prev, open }
              : {
                  open: false,
                  mode: 'add',
                  structureId: null,
                  parentTopWeight: 0,
                  editState: null,
                }
          )
        }
      >
        <DialogContent className="sm:max-w-[560px]" showCloseButton={false}>
          <DialogHeader>
            <div className="flex items-center justify-between gap-3">
              <DialogTitle>
                {subStructureDialog.mode === 'add'
                  ? `Add ${subStructureDialogTypeLabel}`
                  : `Edit ${subStructureDialogTypeLabel}`}
              </DialogTitle>
              <button
                type="button"
                className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-muted/70 hover:bg-muted"
                onClick={() =>
                  setSubStructureDialog({
                    open: false,
                    mode: 'add',
                    structureId: null,
                    parentTopWeight: 0,
                    editState: null,
                  })
                }
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <p className="text-sm font-medium">Title</p>
              <Input
                value={subStructureTitleDraft}
                onChange={(event) => setSubStructureTitleDraft(event.target.value)}
                placeholder="Example: Exam 1"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <p className="text-sm font-medium">Full marks</p>
                <Input
                  type="number"
                  value={subStructureFullMarksDraft}
                  onChange={(event) => setSubStructureFullMarksDraft(event.target.value)}
                  placeholder="E.g: 100"
                />
              </div>
              <div className="space-y-1.5">
                <p className="text-sm font-medium">Weightage</p>
                <Input
                  type="number"
                  value={subStructureWeightageDraft}
                  onChange={(event) => setSubStructureWeightageDraft(event.target.value)}
                  placeholder="10%"
                />
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                type="button"
                onClick={() => void handleSaveSubStructure()}
                disabled={isCreatingSubStructure || isPatchingSubStructure}
              >
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={marksDialogOpen}
        onOpenChange={(open) => {
          setMarksDialogOpen(open);
          if (!open) {
            resetMarksDialogState();
          }
        }}
      >
        <DialogContent className="sm:max-w-4xl" showCloseButton={false}>
          <DialogHeader>
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-1">
                <DialogTitle>Update marks</DialogTitle>
                <p className="text-sm text-muted-foreground">
                  {marksDialogMarkingScheme?.marking_structure_name || 'Subcategory'} learners
                  enrolled in this course.
                </p>
              </div>
              <button
                type="button"
                className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-muted/70 hover:bg-muted"
                onClick={() => setMarksDialogOpen(false)}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </DialogHeader>

          <div className="rounded-2xl border bg-gradient-to-br from-background via-background to-muted/30 p-4 shadow-sm">
            <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span className="rounded-full border bg-background px-2.5 py-1">
                Full marks: {Number(marksDialogMarkingScheme?.full_marks || 0)}
              </span>
              <span className="rounded-full border bg-background px-2.5 py-1">
                Max per student is capped to full marks
              </span>
              <span className="rounded-full border bg-background px-2.5 py-1">
                {visibleStudentDrafts.length} / {studentMarkDrafts.length} visible
              </span>
            </div>

            <div className="mb-3">
              <Input
                value={marksSearch}
                onChange={(event) => setMarksSearch(event.target.value)}
                placeholder="Search learner name or email..."
                className="h-10"
              />
            </div>

            <div className="max-h-[58vh] overflow-auto rounded-xl border bg-card">
              <table className="w-full border-collapse text-sm">
                <thead className="sticky top-0 z-10 bg-muted/90 text-left backdrop-blur">
                  <tr className="text-muted-foreground">
                    <th className="px-4 py-3 font-medium">Learner</th>
                    <th className="px-4 py-3 font-medium">Email</th>
                    <th className="px-4 py-3 font-medium text-right">Marks</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleStudentDrafts.length ? (
                    visibleStudentDrafts.map((student) => {
                      const index = studentMarkDrafts.findIndex(
                        (item) => item.enrollmentId === student.enrollmentId
                      );
                      return (
                      <tr key={student.enrollmentId} className="border-t transition-colors hover:bg-muted/30">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                              {student.name
                                .split(' ')
                                .filter(Boolean)
                                .slice(0, 2)
                                .map((part) => part[0]?.toUpperCase())
                                .join('')}
                            </div>
                            <div>
                              <p className="font-medium">{student.name}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{student.email}</td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end">
                            <Input
                              type="number"
                              min={0}
                              max={Number(marksDialogMarkingScheme?.full_marks || 0)}
                              value={student.marks}
                              onChange={(event) => {
                                const next = event.target.value;
                                setStudentMarkDrafts((prev) =>
                                  prev.map((item, itemIndex) =>
                                    itemIndex === index ? { ...item, marks: next } : item
                                  )
                                );
                              }}
                              placeholder="0"
                              className={`h-9 w-28 text-right ${
                                student.marks.trim() !==
                                (initialMarksByEnrollmentId[student.enrollmentId] ?? '').trim()
                                  ? 'border-emerald-400/60 bg-emerald-50/40 focus-visible:border-emerald-500 focus-visible:ring-emerald-500/20 dark:bg-emerald-950/20'
                                  : ''
                              }`}
                            />
                          </div>
                        </td>
                      </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td className="px-4 py-10 text-center text-muted-foreground" colSpan={3}>
                        {marksSearch.trim()
                          ? 'No learners match your search.'
                          : 'No enrolled learners found for this course.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex items-center justify-between gap-3">
              <p className="text-xs text-muted-foreground">
                Leaving a marks field empty will default it to <span className="font-medium text-foreground">0</span>.
              </p>
              <Button
                type="button"
                onClick={() => void handleSubmitMarks(false)}
                disabled={isUpdatingMarks || !marksDialogMarkingScheme || !hasUnsavedChanges}
              >
                Submit marks
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDefaultMarksOpen} onOpenChange={setConfirmDefaultMarksOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Default empty marks to 0?</AlertDialogTitle>
            <AlertDialogDescription>
              One or more marks fields are empty. If you continue, those values will be submitted as 0.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isUpdatingMarks}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              disabled={isUpdatingMarks}
              onClick={(event) => {
                event.preventDefault();
                void handleSubmitMarks(true);
              }}
            >
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={subMarkingSchemeIdToDelete !== null}
        onOpenChange={(open) => {
          if (!open) {
            setSubMarkingSchemeIdToDelete(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete sub-assessment?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingMarkingScheme}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isDeletingMarkingScheme}
              onClick={(event) => {
                event.preventDefault();
                void handleDeleteSubMarkingScheme();
              }}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={markingSchemeIdToDelete !== null}
        onOpenChange={(open) => {
          if (!open) {
            setMarkingSchemeIdToDelete(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete assessment structure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingMarkingScheme}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isDeletingMarkingScheme}
              onClick={(event) => {
                event.preventDefault();
                void handleDeleteTop();
              }}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CourseAssessmentStructureContent;
